import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Flag, Plus, X, Loader2, GripVertical, Trash2, Pencil, Check } from "lucide-react";

/**
 * Flag template management — four defaults are seeded server-side on first
 * fetch so a brand-new tenant sees sensible chips. Order persists on the
 * server; the UI exposes simple up/down controls instead of HTML5 drag so
 * we don't have to depend on another DnD library.
 */
export default function FlagTemplatesCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [editing, setEditing] = useState({}); // { [id]: label }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/flag-templates");
      setItems(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load templates");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    try {
      const r = await api.post("/flag-templates", { label: newLabel.trim() });
      setItems((arr) => [...arr, r.data]);
      setNewLabel("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.label}"?`)) return;
    try {
      await api.delete(`/flag-templates/${t.id}`);
      setItems((arr) => arr.filter((x) => x.id !== t.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const saveLabel = async (t) => {
    const next = (editing[t.id] || "").trim();
    if (!next || next === t.label) {
      setEditing((e) => ({ ...e, [t.id]: undefined }));
      return;
    }
    try {
      const r = await api.patch(`/flag-templates/${t.id}`, { label: next });
      setItems((arr) => arr.map((x) => x.id === t.id ? r.data : x));
      setEditing((e) => ({ ...e, [t.id]: undefined }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const move = async (idx, direction) => {
    const target = idx + direction;
    if (target < 0 || target >= items.length) return;
    const a = items[idx];
    const b = items[target];
    // Optimistic swap on the client, then persist each row's new order.
    setItems((arr) => {
      const copy = [...arr];
      copy[idx] = { ...b, order: a.order };
      copy[target] = { ...a, order: b.order };
      return copy.sort((x, y) => x.order - y.order);
    });
    try {
      await Promise.all([
        api.patch(`/flag-templates/${a.id}`, { order: b.order }),
        api.patch(`/flag-templates/${b.id}`, { order: a.order }),
      ]);
    } catch { /* server will reconcile on next load */ load(); }
  };

  return (
    <div className="ll-card-flat p-6 mb-6" data-testid="flag-templates-card">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Reconciliation</div>
          <div className="font-display font-bold text-2xl mt-1 flex items-center gap-2">
            <Flag size={18} /> Flag follow-up templates
          </div>
          <p className="text-sm text-[var(--ll-ink-soft)] mt-1 max-w-xl">
            Reusable notes that show up as one-click chips in the verify panel. Perfect for recurring reviewer questions.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="py-6 text-center text-sm text-[var(--ll-ink-soft)]">
          <Loader2 size={16} className="mx-auto animate-spin mb-2" /> Loading…
        </div>
      ) : (
        <ul className="divide-y mb-4" style={{ borderColor: "var(--ll-border)" }} data-testid="flag-templates-list">
          {items.map((t, idx) => {
            const isEditing = editing[t.id] !== undefined;
            return (
              <li key={t.id} className="py-2 flex items-center gap-2" data-testid={`flag-template-row-${t.id}`}>
                <div className="flex items-center gap-0.5 opacity-60">
                  <button
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    data-testid={`flag-template-up-${t.id}`}
                    className="text-xs px-1 py-0.5 disabled:opacity-30"
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => move(idx, 1)}
                    disabled={idx === items.length - 1}
                    data-testid={`flag-template-down-${t.id}`}
                    className="text-xs px-1 py-0.5 disabled:opacity-30"
                    title="Move down"
                  >
                    ↓
                  </button>
                </div>
                <GripVertical size={12} className="opacity-30" />
                {isEditing ? (
                  <input
                    value={editing[t.id]}
                    onChange={(e) => setEditing((es) => ({ ...es, [t.id]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") saveLabel(t); if (e.key === "Escape") setEditing((es) => ({ ...es, [t.id]: undefined })); }}
                    autoFocus
                    data-testid={`flag-template-edit-${t.id}`}
                    className="flex-1 px-2 py-1 border text-sm"
                    style={{ borderColor: "var(--ll-border)" }}
                  />
                ) : (
                  <span className="flex-1 text-sm">{t.label}</span>
                )}
                {isEditing ? (
                  <button
                    onClick={() => saveLabel(t)}
                    data-testid={`flag-template-save-${t.id}`}
                    className="p-1 border hover:bg-[var(--ll-mint)]"
                    style={{ borderColor: "var(--ll-border)" }}
                    title="Save"
                  >
                    <Check size={12} />
                  </button>
                ) : (
                  <button
                    onClick={() => setEditing((es) => ({ ...es, [t.id]: t.label }))}
                    data-testid={`flag-template-edit-btn-${t.id}`}
                    className="p-1 border hover:bg-[var(--ll-mint)]"
                    style={{ borderColor: "var(--ll-border)" }}
                    title="Edit"
                  >
                    <Pencil size={12} />
                  </button>
                )}
                <button
                  onClick={() => remove(t)}
                  data-testid={`flag-template-remove-${t.id}`}
                  className="p-1 border hover:bg-[#FEE2E2] hover:text-[#991B1B]"
                  style={{ borderColor: "var(--ll-border)" }}
                  title="Delete"
                >
                  <Trash2 size={12} />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <form onSubmit={add} className="flex items-center gap-2" data-testid="flag-template-add-form">
        <input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="New follow-up reason…"
          maxLength={80}
          data-testid="flag-template-new"
          className="flex-1 px-3 py-2 border text-sm"
          style={{ borderColor: "var(--ll-border)" }}
        />
        <button
          type="submit"
          disabled={!newLabel.trim()}
          data-testid="flag-template-add"
          className="text-sm px-3 py-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] disabled:opacity-50 flex items-center gap-1.5"
        >
          <Plus size={14} /> Add
        </button>
      </form>
    </div>
  );
}
